"use client";

import { useEffect, useState } from "react";
import Logo from "./Logo";
import SelectField from "./SelectField";
import Button from "./Button";
import { useGateHold } from "./Gate";
import { AccountView } from "@/lib/types";
import { worldIntro } from "../worlds/registry";

/** 一覧を読む。読めない場合は文言を投げる */
async function fetchList(url: string, key: "worlds" | "regions"): Promise<string[]> {
  const response = await fetch(url);
  const body = await response.json();
  if (!response.ok) throw new Error(body.error ?? "一覧を読めません");
  return body[key] as string[];
}

/** キャラクター新規作成画面。世界とリージョンを選ぶ */
export default function CharacterNew({
  account,
  onDecide,
  onCancel,
  onLogout,
}: {
  account: AccountView;
  onDecide: (world: string, region: string) => void;
  onCancel: () => void;
  onLogout: () => void;
}) {
  const [worlds, setWorlds] = useState<string[]>([]);
  const [regions, setRegions] = useState<string[]>([]);
  const [world, setWorld] = useState("");
  const [region, setRegion] = useState("");
  const [message, setMessage] = useState("");
  const [loadingWorlds, setLoadingWorlds] = useState(true);
  const [loadingRegions, setLoadingRegions] = useState(false);
  // 世界の一覧を読み終えるまで、ロード画面に待ってもらう
  useGateHold(loadingWorlds);

  useEffect(() => {
    let alive = true;
    fetchList("/api/worlds", "worlds")
      .then((list) => {
        if (!alive) return;
        setWorlds(list);
        if (list.length === 1) setWorld(list[0]);
        if (list.length === 0) setMessage("新規作成を受け付けている世界がありません");
      })
      .catch((e: Error) => {
        if (alive) setMessage(e instanceof TypeError ? "サーバーに繋がりません" : e.message);
      })
      .finally(() => {
        if (alive) setLoadingWorlds(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    setRegion("");
    setRegions([]);
    if (world === "") return;

    let alive = true;
    setLoadingRegions(true);
    fetchList(`/api/regions?world=${encodeURIComponent(world)}`, "regions")
      .then((list) => {
        if (!alive) return;
        setRegions(list);
        if (list.length === 1) setRegion(list[0]);
      })
      .catch((e: Error) => {
        if (alive) setMessage(e instanceof TypeError ? "サーバーに繋がりません" : e.message);
      })
      .finally(() => {
        if (alive) setLoadingRegions(false);
      });
    return () => {
      alive = false;
    };
  }, [world]);

  const Intro = world === "" ? null : worldIntro(world);

  return (
    <div className="panel panel-wide panel-scene panel-select">
      <div className="head head-brand">
        <Logo />
        <div className="head-account">
          <span className="head-user">
            ユーザー名
            <strong>{account.displayName}</strong>
          </span>
          <span className="head-links">
            <button className="head-link" type="button" onClick={onLogout}>
              ログアウト
            </button>
          </span>
        </div>
      </div>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          onDecide(world, region);
        }}
      >
        <SelectField
          id="new-world"
          label="世界"
          value={world}
          onChange={setWorld}
          options={worlds}
          placeholder={loadingWorlds ? "読み込み中・・・" : "世界を選んでください"}
          disabled={loadingWorlds || worlds.length === 0}
          required
        />

        <SelectField
          id="new-region"
          label="リージョン"
          value={region}
          onChange={setRegion}
          options={regions}
          placeholder={loadingRegions ? "読み込み中・・・" : world === "" ? "先に世界を選んでください" : "リージョンを選んでください"}
          disabled={loadingRegions || regions.length === 0}
          required
        />

        <div className="actions actions-inline">
          <Button type="submit" variant="primary" disabled={world === "" || region === ""}>
            決定
          </Button>
          <Button onClick={onCancel}>戻る</Button>
        </div>
      </form>

      <p className="message message-info">{message}</p>

      {Intro && (
        <div className="world-intro" key={world}>
          <p className="world-intro-name">{world}</p>
          <Intro />
        </div>
      )}
    </div>
  );
}
